import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import AdminLayout from "@/components/Layout";
import {
  Bell, Send, Loader2, Users as UsersIcon, BellRing, History, Info,
  CheckCircle2, AlertTriangle,
} from "lucide-react";

/**
 * Phase 10 — Admin Notifications page.
 *
 * Single-screen broadcast composer + stats panel:
 *   - Subscriber stats (total + active customers).
 *   - Compose form (title, body, optional URL, segment selector).
 *   - "Send to all subscribers" CTA — dispatches the broadcast and shows
 *     the per-call result (sent / failed / pruned).
 *   - History list — last 10 broadcasts pulled from /admin/notifications/stats.
 *
 * Operator routes here from the sidebar "Bildirimler" entry.
 */
export default function Notifications() {
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sendBusy, setSendBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null); // { sent, failed, pruned, total }

  const [title, setTitle]     = useState("");
  const [body, setBody]       = useState("");
  const [url, setUrl]         = useState("/dashboard");
  const [segment, setSegment] = useState("all");

  const refresh = async () => {
    setBusy(true);
    try {
      const r = await api.get("/saas/admin/notifications/stats");
      setStats(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "İstatistikler alınamadı.");
    } finally { setBusy(false); }
  };

  useEffect(() => { refresh(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setResult(null);
    if (!title.trim() || !body.trim()) {
      setErr("Başlık ve mesaj zorunlu.");
      return;
    }
    setSendBusy(true);
    try {
      const r = await api.post("/saas/admin/notifications/send", {
        title:   title.trim(),
        body:    body.trim(),
        url:     (url || "/dashboard").trim(),
        segment,
      });
      setResult(r.data);
      // Clear composer on success
      if ((r.data?.total ?? 0) > 0) {
        setTitle(""); setBody("");
      }
      refresh();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Bildirim gönderilemedi.");
    } finally { setSendBusy(false); }
  };

  return (
    <AdminLayout
      title="Bildirimler"
      breadcrumb={["Sistem", "Bildirimler"]}
    >
      <div className="space-y-6 max-w-5xl" data-testid="admin-notifications-page">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <StatCard
            label="Push Aboneleri"
            value={stats?.subscribers?.total ?? "—"}
            icon={BellRing} accent="amber"
            data-testid="notif-stat-total"
          />
          <StatCard
            label="Aktif Müşteri Aboneleri"
            value={stats?.subscribers?.active_customers ?? "—"}
            icon={UsersIcon} accent="emerald"
            data-testid="notif-stat-active"
          />
          <StatCard
            label="Geçmiş Yayın"
            value={stats?.history?.length ?? "—"}
            icon={History} accent="white"
            data-testid="notif-stat-history"
          />
        </div>

        {/* Composer */}
        <div className="bg-slate-950/60 border border-slate-800 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-5">
            <Bell className="w-4 h-4 text-amber-400" />
            <h2 className="text-base font-bold text-white">Yeni Bildirim Yayını</h2>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="broadcast-form">
            <Field label="Başlık" hint='Örn: "Derbi maçı başladı! 🏆"'>
              <input
                type="text" maxLength={50}
                value={title} onChange={(e) => setTitle(e.target.value)}
                placeholder="Bildirim başlığı (max 50 karakter)"
                className="w-full bg-slate-950 border border-slate-800 rounded-lg p-3 text-white text-sm focus:border-amber-500 outline-none"
                data-testid="broadcast-title"
              />
            </Field>

            <Field label="Mesaj">
              <textarea
                rows={3} maxLength={150}
                value={body} onChange={(e) => setBody(e.target.value)}
                placeholder='Örn: "Galatasaray vs Fenerbahçe — Süper Lig Derbisi şu an canlı yayında"'
                className="w-full bg-slate-950 border border-slate-800 rounded-lg p-3 text-white text-sm focus:border-amber-500 outline-none resize-none"
                data-testid="broadcast-body"
              />
              <p className="text-[10px] text-gray-500 mt-1">{body.length}/150 karakter</p>
            </Field>

            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Tıklama URL'si">
                <input
                  type="text"
                  value={url} onChange={(e) => setUrl(e.target.value)}
                  placeholder="/dashboard"
                  className="w-full bg-slate-950 border border-slate-800 rounded-lg p-3 text-white text-sm focus:border-amber-500 outline-none font-mono"
                  data-testid="broadcast-url"
                />
              </Field>
              <Field label="Hedef Segment">
                <select
                  value={segment} onChange={(e) => setSegment(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 rounded-lg p-3 text-white text-sm focus:border-amber-500 outline-none"
                  data-testid="broadcast-segment"
                >
                  <option value="all">Tüm Aboneler</option>
                  <option value="active">Sadece Aktif Müşteriler</option>
                </select>
              </Field>
            </div>

            {err && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-xs" data-testid="broadcast-err">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                {err}
              </div>
            )}
            {result && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs" data-testid="broadcast-result">
                <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <div>
                  Gönderim tamamlandı: <strong className="text-white">{result.sent}</strong> başarılı
                  · <strong className="text-white">{result.failed}</strong> başarısız
                  · <strong className="text-white">{result.pruned}</strong> silinmiş abonelik temizlendi
                  (toplam {result.total})
                </div>
              </div>
            )}

            <div className="flex items-center justify-between pt-2 border-t border-slate-800">
              <p className="text-[11px] text-gray-500 flex items-center gap-1.5">
                <Info className="w-3 h-3" />
                Bildirimler {stats?.subscribers?.total ?? 0} cihaza ulaşacak
              </p>
              <button
                type="submit"
                disabled={sendBusy}
                className="h-11 px-5 rounded-lg bg-amber-500 text-black font-bold text-sm hover:bg-amber-400 transition disabled:opacity-50 inline-flex items-center gap-2 active:translate-y-[1px]"
                data-testid="broadcast-submit"
              >
                {sendBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {sendBusy ? "Gönderiliyor…" : "Bildirimi Gönder"}
              </button>
            </div>
          </form>
        </div>

        {/* History */}
        {(stats?.history || []).length > 0 && (
          <div className="bg-slate-950/60 border border-slate-800 rounded-2xl p-6" data-testid="broadcast-history">
            <div className="flex items-center gap-2 mb-4">
              <History className="w-4 h-4 text-amber-400" />
              <h2 className="text-base font-bold text-white">Son Yayınlar</h2>
            </div>
            <div className="divide-y divide-slate-800">
              {stats.history.map((h, i) => (
                <div key={i} className="py-3 flex items-start gap-3 text-sm">
                  <Bell className="w-3.5 h-3.5 text-amber-400 mt-1 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <strong className="text-white">{h.title}</strong>
                      <span className="text-[10px] font-mono uppercase tracking-widest text-gray-500">{h.segment}</span>
                    </div>
                    <p className="text-xs text-gray-400 truncate mt-0.5">{h.body}</p>
                    <p className="text-[10px] font-mono text-gray-600 mt-1">
                      {new Date(h.created_at).toLocaleString()} · {h.admin}
                    </p>
                  </div>
                  <div className="text-[10px] font-mono text-right shrink-0 text-gray-500 leading-tight">
                    <span className="text-emerald-400">↑{h.result?.sent ?? 0}</span>
                    {" "}
                    <span className="text-red-400">↓{h.result?.failed ?? 0}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function StatCard({ label, value, icon: Icon, accent, "data-testid": testId }) {
  const cmap = { amber: "text-amber-400", emerald: "text-emerald-400", white: "text-white" };
  return (
    <div className="bg-slate-950/50 border border-slate-800 rounded-xl p-4" data-testid={testId}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-gray-500 font-bold">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className={`text-3xl font-bold mt-1 ${cmap[accent] || "text-white"}`}>{value}</div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">{label}</span>
      <div className="mt-1.5">{children}</div>
      {hint && <p className="text-[10px] text-gray-500 mt-1">{hint}</p>}
    </label>
  );
}
